import React, { useCallback, useState } from 'react';
import { Modal } from 'is24-corecss';
import { Benefit } from '../../../../../@types/benefits';
import { BenefitModalProps } from '../../../../../@types/benefitModalProps';
import LagerraumJpg from './images/storage.jpg';
import LagerraumWebp from './images/storage.webp';
import { EntitlementType } from '../../../../../@types/entitlement';
import { useGetHasEntitlement } from '../../../../../customHooks/useGetHasEntitlement.hook';
import { ProductsStatusKey } from '../../../../../@types/productsStatus';
import { useProductStatus } from '../../../../../customHooks/useProductStatus.hook';
import { STORAGEBOX_SERVICE_STATUS, StorageboxData, StorageboxState } from '../../../../../@types/storagebox';
import LagerraumForm from './components/LagerraumForm/LagerraumForm';
import HowItWorksTab from './components/HowItWorksTab';
import OverviewTab from './components/OverviewTab';
import { OverviewTrackingEvents } from '../../../../../tracking/trackingEvents';
import { useHasMeinPlus } from '../../../../../customHooks/useHasMeinPlus.hook';
import { useRegisterStoragebox } from '../../../../../apiHooks/storagebox.hook';
import { report } from '../../../../../tracking/tracking';
import FAQTab from './components/FAQTab';
import { useIntl } from '@/i18n/hooks/useIntl';
import { campaigns } from '@/components/common/campaigns';
import { useUrlState } from '@/customHooks/useUrlState.hook';
import ModalV2 from '@/components/common/display/modal/ModalV2';

export const Lagerraum = ({ isVisible, onClose }: BenefitModalProps) => {
  const { t } = useIntl();
  const [showForm, setShowForm] = useState(false);
  const handleCloseForm = () => setShowForm(false);
  const isEntitled = useGetHasEntitlement(EntitlementType.STORAGE_SERVICE);
  const product = useProductStatus(ProductsStatusKey.Lagerbox);
  const isMeinPlus = useHasMeinPlus();
  const { mutate, isLoading } = useRegisterStoragebox();
  const storageboxStateService = (product as StorageboxState)?.status;
  const status = storageboxStateService || STORAGEBOX_SERVICE_STATUS.NOT_ACTIVATED;
  const { currentUrl, isMFE } = useUrlState();

  const goToForm = useCallback(async () => {
    await report(OverviewTrackingEvents.STORAGE_SERVICE.FORM_VISITED, isMeinPlus);
    setShowForm(true);
  }, [setShowForm, report]);

  const handleSubmitCallback = useCallback(
    async (values: StorageboxData) => {
      try {
        // TODO: check this API call is working properly
        await mutate(values);
        setShowForm(false);
        // TODO: check with Diego about ga_cd_profile_status (isBuyerPlus)
        await report(OverviewTrackingEvents.STORAGE_SERVICE.FUNNEL_SUCCESS, isMeinPlus);
      } catch (e) {}
    },
    [mutate, isMeinPlus, onClose],
  );

  const shouldShowPrimaryButton = status !== STORAGEBOX_SERVICE_STATUS.ACTIVATED && !showForm;

  return (
    <ModalV2
      title={t({ id: 'benefits.storageService.modal.headline', defaultMessage: 'Lagerraum' })}
      isVisible={isVisible}
      image={LagerraumJpg}
      imageWebp={LagerraumWebp}
      imageMobile={LagerraumJpg}
      imageMobileWebp={LagerraumWebp}
      imageUseFullWidth={true}
      imageAlt={t({
        id: 'benefits.storageService.modal.image',
        defaultMessage: 'Woman using mortgage manager',
      })}
      tabsContent={[
        <OverviewTab
          key='overview'
          status={status}
          isEntitled={isEntitled}
          url={`${campaigns[isMFE ? 'LAGERRAUM_MFE' : 'LAGERRAUM']}${isMFE && currentUrl ? `&returnUrl=${encodeURIComponent(currentUrl)}` : ''}`}
        />,
        <HowItWorksTab key='howItWorks' />,
        <FAQTab key='faq' benefit='storageService' />,
      ]}
      formData={
        showForm ? (
          <LagerraumForm close={handleCloseForm} onSubmit={handleSubmitCallback} isLoading={isLoading} />
        ) : null
      }
      benefit={Benefit.STORAGE_SERVICE}
      onClose={onClose}
      primaryFooterBtn={
        shouldShowPrimaryButton ? (
          <Modal.FooterPrimaryBtn
            label={
              isEntitled
                ? t({
                    id: 'benefits.storageService.modal.isEntitledLinkButton',
                    defaultMessage: 'Unverbindliches Angebot anfordern',
                  })
                : t({ id: 'benefits.storageService.modal.linkButton', defaultMessage: 'Mit 5% Rabatt fortfahren' })
            }
            onClick={goToForm}
          />
        ) : undefined
      }
    />
  );
};

export default Lagerraum;
